class SubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.addToCartBtn = this.querySelector('[data-subscription-add-to-cart]');
    this.subscriptionOptions = Array.from(this.querySelectorAll('.subscription-option'));
    this.selectedOption = null;
  }

  connectedCallback() {
    if (!this.addToCartBtn) return;
    
    // Initialize with the first option (one-time purchase) selected
    this.selectedOption = this.subscriptionOptions.find(option => option.classList.contains('subscription-option--selected'));
    if (!this.selectedOption) {
      this.selectedOption = this.subscriptionOptions[0];
    }
    
    // Add click handlers to all subscription options
    this.subscriptionOptions.forEach(option => {
      option.addEventListener('click', this.handleOptionClick.bind(this));
    });
    
    this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
  }

  handleOptionClick(event) {
    // Remove selected class from all options
    this.subscriptionOptions.forEach(option => {
      option.classList.remove('subscription-option--selected');
    });
    
    // Add selected class to clicked option
    event.currentTarget.classList.add('subscription-option--selected');
    
    // Update selected option
    this.selectedOption = event.currentTarget;
  }

  handleAddToCart() {
    if (!this.selectedOption) {
      console.error('No purchase option selected');
      return;
    }
    
    const button = this.addToCartBtn;
    const buttonText = button.querySelector('span');
    const originalText = buttonText.textContent;
    
    // Show loading state
    button.classList.add('loading');
    button.disabled = true;
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
    
    // Determine which product to add
    let productId = null;
    let sellingPlanId = null;
    const optionType = this.selectedOption.dataset.optionType;
    
    if (optionType === 'onetime') {
      // Add main product
      productId = this.selectedOption.dataset.productId;
      sellingPlanId = this.selectedOption.dataset.sellingPlanId;
    } else {
      // For subscription products, get the product ID and selling plan ID
      productId = this.selectedOption.dataset.productId;
      sellingPlanId = this.selectedOption.dataset.sellingPlanId;
      
      // Check if there's a checkbox product selected
      const checkboxInput = this.selectedOption.querySelector('input[type="checkbox"]:checked');

      if (checkboxInput) {
        // Add checkbox product with its own selling plan
        productId = checkboxInput.value;
        sellingPlanId = checkboxInput.dataset.sellingPlanId || null;
      }
    }
    
    if (!productId || productId === 'undefined' || productId === 'null') {
      console.error('No valid product ID found:', productId);
      this.resetButton(button, buttonText, originalText);
      return;
    }
    
    // Validate product ID is a number
    const numericProductId = parseInt(productId);
    if (isNaN(numericProductId)) {
      console.error('Product ID is not a valid number:', productId);
      this.resetButton(button, buttonText, originalText);
      return;
    }
    
    // Use the numeric product ID
    productId = numericProductId;
    
    // Prepare the request body
    const requestBody = {
      id: productId,
      quantity: 1
    };
    
    // Only add selling_plan if it's valid
    if (sellingPlanId && sellingPlanId !== 'null' && sellingPlanId !== 'undefined') {
      const numericSellingPlanId = parseInt(sellingPlanId);
      if (!isNaN(numericSellingPlanId)) {
        requestBody.selling_plan = numericSellingPlanId;
      } else {
        console.warn('Invalid selling plan ID:', sellingPlanId);
      }
    }

    const giftNoteModalOpener = this.querySelector('[data-modal="#PopupModal-product-gift-note"]');
    if (giftNoteModalOpener) {
      const giftNoteModal = document.querySelector(giftNoteModalOpener.dataset.modal);
      if (giftNoteModal) {
        const idInput = giftNoteModal.querySelector('[name="items[][id]"]');
        const quantityInput = giftNoteModal.querySelector('[name="items[][quantity]"]');
        const attachedToInput = giftNoteModal.querySelector('[name="items[][properties][AttachedTo]"]');
        const giftMessageInput = giftNoteModal.querySelector('[name="items[][properties][GiftMessage]"]');
        if (idInput && quantityInput && attachedToInput && giftMessageInput && giftMessageInput.getAttribute('form')) {
          requestBody.items = [
            {
              id: idInput.value,
              quantity: quantityInput.value,
              properties: {
                AttachedTo: attachedToInput.value,
                GiftMessage: giftMessageInput.value
              }
            }
          ];
        }
      }
    }

    let responseReceived = false;
    let hasError = false;
    const cart = document.querySelector('cart-drawer');

    const loadingTimer = setTimeout(() => {
      if (!responseReceived && !hasError && cart) {
        const productInfo = this.getProductInfo();
        cart.openWithSkeleton(productInfo);
      }
    }, 500);

    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.text().then(text => {
        if (!text || text.trim() === '') {
          throw new Error('Empty response from server');
        }
        return JSON.parse(text);
      });
    })
    .then(data => {
      responseReceived = true;
      clearTimeout(loadingTimer);

      if (data.status === 422) {
        hasError = true;
        throw new Error(data.description || 'Product could not be added to cart');
      }

      if (cart) {
        if (cart.isOpen && cart.hasSkeletonActive) {
          fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`)
            .then(response => response.json())
            .then(sections => {
              cart.updateFromSkeleton({ sections });
            });
        } else {
          document.dispatchEvent(new CustomEvent('cart-drawer:refresh', {
            detail: { 'response': data },
            bubbles: true,
            composed: true
          }));
          
          if (!cart.isOpen) {
            cart.toggleDrawerClasses();
          }
        }
      }

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "subscription-widget",
          productVariantId: productId,
          cartData: data,
        });
      }
      
      this.resetButton(button, buttonText, originalText);
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      responseReceived = true;
      hasError = true;
      clearTimeout(loadingTimer);

      if (cart && cart.isOpen && cart.hasSkeletonActive) {
        cart.closeOnError(error.message);
      }

      this.resetButton(button, buttonText, originalText);
    });
  }

  getProductInfo() {
    const productName = document.querySelector('.wt-product__name')?.textContent.trim() || 'Product';
    const productImage = document.querySelector('.wt-product__img img')?.src || '';
    const productPrice = document.querySelector('.wt-product__price .price-item--regular')?.textContent.trim() || '';
    const variantOptions = Array.from(document.querySelectorAll('.wt-product__option__title .value'));
    const variant = variantOptions.map(opt => opt.textContent.trim()).join(' / ');

    return {
      name: productName,
      image: productImage,
      price: productPrice,
      variant: variant,
      quantity: 1
    };
  }
  
  resetButton(button, buttonText, originalText) {
    button.classList.remove('loading');
    button.disabled = false;
    buttonText.textContent = originalText;
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }
}

customElements.define('subscription-widget', SubscriptionWidget);
